import { normalizeUnitOfMeasure, quantityAllowsFraction } from '@shared/quantity';
import type { BulkPreviewItem, ProductMutationOpts } from '../domain/catalogTypes';
import { getSessionOrganizationId, requireSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { getCatalogRamSnapshotCache, patchWebCatalogProductInCache } from './catalogRamSnapshotCache';
import { appendProductCatalogEvent } from './productCatalogEventWriter';
import { submitCommand } from '../../sync/core/submitCommand';
import { matchesSearchAny } from '../../shared/format/searchText';

type RamProductRow = {
  id?: unknown;
  organization_id?: unknown;
  is_misc?: unknown;
  item_type?: unknown;
  is_active?: unknown;
  category_id?: unknown;
  name?: unknown;
  sku?: unknown;
  barcode?: unknown;
  price?: unknown;
  unit_of_measure?: unknown;
};

type RamStockRow = { product_id?: unknown; branch_id?: unknown; quantity?: unknown };

/**
 * Alcance compartido del bulk: productos físicos, activos, no-misc de la org,
 * filtrados por categoría (opcional) y búsqueda (nombre/SKU/código de barras).
 */
function filterBulkScopeRows(
  products: RamProductRow[],
  organizationId: string,
  categoryId: string | 'all' | null | undefined,
  search: string,
): RamProductRow[] {
  return products
    .filter((p) => String(p.organization_id ?? '') === organizationId)
    .filter((p) => p.is_misc !== true && p.item_type !== 'service' && p.is_active !== false)
    .filter((p) => categoryId === 'all' || !categoryId || String(p.category_id ?? '') === categoryId)
    .filter((p) =>
      matchesSearchAny(
        [p.name, p.sku, p.barcode].map((v) => (v == null ? null : String(v))),
        search,
      ),
    );
}

export type BulkScopeCounts = {
  /** Ítems del alcance con precio cargado (> 0): son los que toca el coeficiente. */
  priced: number;
  /** Ítems del alcance sin precio (0 o vacío): quedan igual con cualquier %. */
  unpriced: number;
};

/**
 * Conteo del alcance para copy honesto («Todos con precio (N) · M sin precio no se tocan»).
 * Sin límite: es un conteo sobre el snapshot RAM, no la página de la vista previa.
 */
export const getBulkScopeCounts = async (params: {
  categoryId?: string | null | 'all';
  search?: string;
}): Promise<BulkScopeCounts | null> => {
  const { categoryId = 'all', search = '' } = params;
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return null;
  const products = getCatalogRamSnapshotCache()?.products ?? [];
  const scoped = filterBulkScopeRows(products, organizationId, categoryId, search);
  let priced = 0;
  for (const row of scoped) {
    if (Number(row.price ?? 0) > 0) priced += 1;
  }
  return { priced, unpriced: scoped.length - priced };
};

export const getBulkPreview = async (
  params: {
    categoryId?: string | null | 'all';
    search?: string;
    branchId?: string | null;
    coefficient: number;
    applyTo: 'prices' | 'quantities';
    limit?: number;
  }
): Promise<BulkPreviewItem[]> => {
  const { categoryId = 'all', search = '', branchId, coefficient, applyTo, limit = 500 } = params;
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];

  const products = getCatalogRamSnapshotCache()?.products ?? [];
  const stocks = getCatalogRamSnapshotCache()?.stocks ?? [];
  const scoped = filterBulkScopeRows(products, organizationId, categoryId, search);
  // Precios: los ítems sin precio cargado no entran a la vista previa ni a la
  // lista de apply — con cualquier coeficiente seguirían en 0 («no se tocan»).
  const filtered = (
    applyTo === 'prices'
      ? scoped.filter((row) => Number(row.price ?? 0) > 0)
      : scoped
  ).slice(0, limit);

  if (applyTo === 'prices') {
    return filtered.map((row) => ({
      id: String(row.id),
      name: String(row.name ?? ''),
      currentValue: Number(row.price ?? 0),
      newValue: Math.round(Number(row.price ?? 0) * coefficient * 100) / 100,
    }));
  }

  if (applyTo === 'quantities' && branchId) {
    return filtered.map((row) => {
      const stock = stocks.find((s: RamStockRow) => s.product_id === row.id && s.branch_id === branchId);
      const quantity = Number(stock?.quantity ?? 0);
      const unitOfMeasure = normalizeUnitOfMeasure(row.unit_of_measure);
      const rawNewValue = quantity * coefficient;
      const newValue = quantityAllowsFraction(unitOfMeasure) ? rawNewValue : Math.round(rawNewValue);
      return {
        id: String(row.id),
        name: String(row.name ?? ''),
        currentValue: quantity,
        newValue,
      };
    });
  }
  return [];
};

export const listDistinctProductBrandsLocal = async (): Promise<{ id: string; name: string }[]> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];
  const brands = getCatalogRamSnapshotCache()?.brands ?? [];
  return brands
    .filter((b) => b.id && b.name && String(b.organization_id ?? '') === organizationId)
    .filter((b) => b.is_active !== false && !b.deleted_at)
    .map((b) => ({ id: String(b.id), name: String(b.name ?? '') }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
};

export const bulkUpdatePricesByCoefficient = async (
  productIds: string[],
  coefficient: number,
  opts?: ProductMutationOpts
): Promise<number> => {
  if (productIds.length === 0) return 0;
  const organizationId = await requireSessionOrganizationId();
  const timestamp = new Date().toISOString();
  let updated = 0;
  for (const id of productIds) {
    let row: {
      price: number;
      name: string;
      sku: string;
      barcode: string | null;
      category_id: string | null;
      organization_id: string | null;
      item_type: string | null;
      is_misc: number | null;
      is_active: number | null;
    } | null = null;

    const cached = getCatalogRamSnapshotCache()?.products.find((p) => String(p.id) === id);
    if (!cached) continue;
    row = {
      price: Number(cached.price ?? 0),
      name: String(cached.name ?? ''),
      sku: String(cached.sku ?? ''),
      barcode: cached.barcode != null ? String(cached.barcode) : null,
      category_id: cached.category_id != null ? String(cached.category_id) : null,
      organization_id: cached.organization_id != null ? String(cached.organization_id) : null,
      item_type: cached.item_type === 'service' ? 'service' : 'physical',
      is_misc: cached.is_misc ? 1 : 0,
      is_active: cached.is_active !== false ? 1 : 0,
    };
    if (!row) continue;
    if (row.organization_id && row.organization_id !== organizationId) continue;
    if (row.is_misc === 1 || row.item_type === 'service') continue;
    if (row.is_active === 0) continue;
    // Guardia: sin precio cargado el coeficiente no toca el producto, aunque el
    // id llegue por un estado viejo del modal.
    if (row.price <= 0) continue;
    const newPrice = Math.round(row.price * coefficient * 100) / 100;

    const result = await submitCommand({
      entity: 'product',
      operation: 'update',
      payload: {
        id,
        organizationId,
        name: row.name,
        price: newPrice,
        sku: row.sku,
        barcode: row.barcode ?? null,
        categoryId: row.category_id,
        updatedAt: timestamp,
        isActive: row.is_active !== 0,
        itemType: row.item_type === 'service' ? 'service' : 'physical',
        isMisc: row.is_misc === 1,
      },
    });
    if (!result.applied) {
      captureOpsFailure({
        module: 'catalog',
        op: 'catalog.bulk_price',
        error: new Error(result.ingestError?.trim() || 'bulk_price_not_applied'),
        ingestError: result.ingestError,
        applied: false,
        path: 'web-ram',
        itemId: id,
        extra: { productId: id },
      });
      continue;
    }
    patchWebCatalogProductInCache(id, { price: newPrice, updated_at: timestamp });

    await appendProductCatalogEvent({
      productId: id,
      eventType: 'bulk_price',
      summary: `Precio ${row.price} → ${newPrice} ARS (coef. ${coefficient})`,
      payload: {
        beforePrice: row.price,
        afterPrice: newPrice,
        coefficient,
      },
      performedByUserId: opts?.performedByUserId,
    });
    updated += 1;
  }
  return updated;
};

